import { useCallback, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { allTrackers } from "app/services/track/track";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";

import { createOpportunityListFromSearchThunk, updateListOpportunitiesFromSearchThunk } from "../../../store/effects";
import { selectOpportunityListCreating, selectOpportunityListUpdating } from "../../../store/selectors";
import type { OpportunityListType } from "../../../types";

import AddToListDropdown from "./AddToListDropdown";
import { selectListsWithDisabledProp } from "./selectors";
import type { AddToListDropdownProps } from "./types";

type AddToListDropdownContainerProps = {
    isDisabled?: boolean;
    disabledLists?: string[];
    opportunityList?: OpportunityListType[];
    onFinishedCreatingList?(list: OpportunityListType): void;
    onListUpdate?(): void;
} & Pick<
    AddToListDropdownProps,
    "dropdownItemClick" | "renderDropdownButton" | "domains" | "labelCreateBtn" | "disabledText" | "className"
>;

const AddToListDropdownContainer = (props: AddToListDropdownContainerProps) => {
    const dispatch: ThunkDispatchCommon = useDispatch();
    const isListBeingCreated: boolean = useSelector(selectOpportunityListCreating);
    const isListBeingUpdated: boolean = useSelector(selectOpportunityListUpdating);
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);
    const lists = useSelector(
        useCallback((state: RootState) => selectListsWithDisabledProp(state, props), [props.disabledLists]),
    );

    async function handleListCreate(name: string, domains?: readonly string[]) {
        const createdList = await dispatch(createOpportunityListFromSearchThunk(name, domains));

        if (createdList && props.onFinishedCreatingList) {
            props.onFinishedCreatingList(createdList);
        }

        setIsDropdownOpened(false);
    }

    function handleListUpdate(...args: Parameters<typeof updateListOpportunitiesFromSearchThunk>) {
        allTrackers.dispatchTrackEvent("top page bar", "add to list", "existing");

        void dispatch(updateListOpportunitiesFromSearchThunk(...args));

        props.onListUpdate?.();
    }

    return (
        <AddToListDropdown
            opportunityLists={props.opportunityList || lists}
            isOpen={isDropdownOpened}
            listCreating={isListBeingCreated}
            listUpdating={isListBeingUpdated}
            createList={handleListCreate}
            setIsOpen={setIsDropdownOpened}
            updateListOpportunitiesFromSearch={handleListUpdate}
            {...props}
        />
    );
};

export default AddToListDropdownContainer;
